import { ref } from 'vue';
import { io, type Socket } from 'socket.io-client';
import { COMPONENT_DATA_SUBSCRIPTION_BATCH_SIZE } from 'shared';
import type {
  CollaborationLockPayload,
  CollaborationPresencePayload,
  CollaborationSnapshotPayload,
  RealtimeEventEnvelope,
  MonitorUserFeature,
  ClientNavigationContext,
  AgentViewReadReply,
  AgentViewReadRequest,
} from 'shared';
import { createClientId } from '@/utils/client-id';
import { apiOrigin, getAccessToken } from '@/api/client';

export type { ResourceChangedEvent } from 'shared';
export type RealtimeEvent = RealtimeEventEnvelope;

let socket: Socket | null = null;
let agentViewReader: ((input: AgentViewReadRequest) => Promise<AgentViewReadReply>) | undefined;
const listeners = new Set<(event: RealtimeEvent) => void>();
const joinedWorkspaces = new Map<string, number>();
const joinedAgentSessions = new Map<string, string>();
interface ComponentDataSubscription {
  channel: string;
  subscriptionToken: string;
  count: number;
}
const componentDataSubscriptions = new Map<string, ComponentDataSubscription>();
type SubscriptionRequest = Omit<ComponentDataSubscription, 'count'>;
type PendingSubscription = { input: SubscriptionRequest; resolve: () => void; reject: (error: Error) => void };
let pendingSubscriptions: PendingSubscription[] = [];

function joinComponentData(socket: Socket, input: SubscriptionRequest): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!pendingSubscriptions.length) queueMicrotask(() => {
      const pending = pendingSubscriptions;
      pendingSubscriptions = [];
      for (let offset = 0; offset < pending.length; offset += COMPONENT_DATA_SUBSCRIPTION_BATCH_SIZE) {
        const batch = pending.slice(offset, offset + COMPONENT_DATA_SUBSCRIPTION_BATCH_SIZE);
        socket.timeout(5000).emit(
          'component-data.subscribe-batch',
          batch.map((item) => item.input),
          (error: Error | null, result?: Array<{ channel: string } | null>) => {
            for (const [index, item] of batch.entries()) {
              if (error || result?.[index]?.channel !== item.input.channel)
                item.reject(error ?? new Error('数据实时订阅失败'));
              else item.resolve();
            }
          },
        );
      }
    });
    pendingSubscriptions.push({ input, resolve, reject });
  });
}
const seenEventIds = new Set<string>();
const eventCursors = new Map<string, string>();
const workspacePresence = new Map<
  string,
  Pick<CollaborationPresencePayload, 'pageId' | 'selectedNodeId'>
>();
const ownedLocks = new Map<string, { workspaceId: string; resourceKey: string }>();
const connected = ref(false);
let heartbeat: ReturnType<typeof setInterval> | null = null;
let currentFeature: MonitorUserFeature = 'other';
let currentNavigation: ClientNavigationContext | undefined;

export function updateClientActivity(
  feature: MonitorUserFeature,
  navigation?: ClientNavigationContext,
): void {
  currentFeature = feature;
  currentNavigation = navigation;
  sendActivity();
}
function sendActivity(): void {
  if (socket?.connected)
    socket.emit('activity.update', {
      feature: currentFeature,
      visibility: document.visibilityState === 'hidden' ? 'hidden' : 'visible',
      ...(currentNavigation ? { navigation: currentNavigation } : {}),
    });
}

function token(): string {
  return getAccessToken() ?? '';
}
function cursorKey(workspaceId: string): string {
  return `realtime:last-event:${workspaceId}`;
}
function getCursor(workspaceId: string): string | undefined {
  if (!eventCursors.has(workspaceId))
    eventCursors.set(workspaceId, localStorage.getItem(cursorKey(workspaceId)) ?? '');
  return eventCursors.get(workspaceId) || undefined;
}
function saveCursor(workspaceId: string, eventId: string): void {
  eventCursors.set(workspaceId, eventId);
  localStorage.setItem(cursorKey(workspaceId), eventId);
}
function notify(event: RealtimeEvent): void {
  for (const listener of listeners) listener(event);
}
function localEvent(type: string, workspaceId: string, payload: object): void {
  notify({
    eventId: createClientId(),
    type,
    occurredAt: new Date().toISOString(),
    workspaceId,
    actor: { kind: 'system', id: null },
    resourceRef: null,
    traceId: null,
    schemaVersion: 1,
    sequence: null,
    payload: payload as Record<string, unknown>,
  });
}
function refreshEphemeralState(): void {
  if (!socket?.connected) return;
  sendActivity();
  for (const workspaceId of joinedWorkspaces.keys())
    socket.emit('presence.update', { workspaceId, ...(workspacePresence.get(workspaceId) ?? {}) });
  for (const lock of ownedLocks.values()) socket.emit('lock.acquire', lock);
}

function ensureSocket(): Socket {
  if (socket) return socket;
  socket = io(`${apiOrigin}/collaboration`, {
    path: '/socket.io',
    transports: ['websocket'],
    auth: { token: token() },
    reconnection: true,
    autoConnect: false,
  });
  socket.on('connect', () => {
    connected.value = true;
    socket!.auth = { token: token() };
    sendActivity();
    if (!heartbeat) heartbeat = setInterval(refreshEphemeralState, 15_000);
    for (const workspaceId of joinedWorkspaces.keys())
      socket!.emit('workspace.join', { workspaceId, lastEventId: getCursor(workspaceId) });
    for (const [sessionId, workspaceId] of joinedAgentSessions)
      socket!.emit('agent-session.join', { workspaceId, sessionId });
  });
  socket.on('agent-view.read', (input: AgentViewReadRequest, reply: (value: AgentViewReadReply) => void) => {
    const reader = agentViewReader;
    if (!reader) return reply({ status: 'view_changed' });
    void reader(input).then(reply, () => reply({ status: 'view_changed' }));
  });
  socket.on('disconnect', (reason) => {
    connected.value = false;
    if (heartbeat) clearInterval(heartbeat);
    heartbeat = null;
    if (reason === 'io server disconnect' && token()) {
      socket!.auth = { token: token() };
      socket!.connect();
    }
  });
  document.addEventListener('visibilitychange', sendActivity);
  socket.on('workspace.joined', ({ workspaceId }: { workspaceId: string }) => {
    socket!.emit('presence.update', { workspaceId, ...(workspacePresence.get(workspaceId) ?? {}) });
    for (const lock of ownedLocks.values())
      if (lock.workspaceId === workspaceId) socket!.emit('lock.acquire', lock);
  });
  socket.on('realtime.event', (event: RealtimeEvent) => {
    if (!event?.eventId || seenEventIds.has(event.eventId)) return;
    seenEventIds.add(event.eventId);
    if (seenEventIds.size > 2_000) seenEventIds.clear();
    if (event.workspaceId) saveCursor(event.workspaceId, event.eventId);
    notify(event);
  });
  socket.on('realtime.snapshot.required', ({ workspaceId }: { workspaceId: string }) => {
    eventCursors.delete(workspaceId);
    localStorage.removeItem(cursorKey(workspaceId));
    notify({
      eventId: createClientId(),
      type: 'realtime.snapshot.required',
      occurredAt: new Date().toISOString(),
      workspaceId,
      actor: { kind: 'system', id: null },
      resourceRef: null,
      traceId: null,
      schemaVersion: 1,
      sequence: null,
      payload: {},
    });
  });
  socket.on('presence.changed', (payload: CollaborationPresencePayload) =>
    localEvent('presence.changed', payload.workspaceId, payload),
  );
  socket.on('lock.changed', (payload: CollaborationLockPayload) =>
    localEvent('lock.changed', payload.workspaceId, payload),
  );
  socket.on('collaboration.snapshot', (payload: CollaborationSnapshotPayload) =>
    localEvent('collaboration.snapshot', payload.workspaceId, payload),
  );
  return socket;
}

export function refreshCollaborationAuth(): void {
  if (!socket) return;
  socket.auth = { token: token() };
  if (socket.connected) socket.disconnect();
  if (token()) socket.connect();
}

export function resetCollaborationSubscriptions(): void {
  joinedWorkspaces.clear();
  joinedAgentSessions.clear();
  componentDataSubscriptions.clear();
  workspacePresence.clear();
  ownedLocks.clear();
  seenEventIds.clear();
  for (const workspaceId of eventCursors.keys()) localStorage.removeItem(cursorKey(workspaceId));
  eventCursors.clear();
  currentNavigation = undefined;
  socket?.disconnect();
  connected.value = false;
}

export function useCollaborationSocket() {
  const current = ensureSocket();

  function onAgentViewRead(handler: (input: AgentViewReadRequest) => Promise<AgentViewReadReply>) {
    agentViewReader = handler;
    return () => {
      if (agentViewReader === handler) agentViewReader = undefined;
    };
  }

  function connect(): void {
    if (token()) current.connect();
  }
  function joinWorkspace(workspaceId: string): void {
    const count = joinedWorkspaces.get(workspaceId) ?? 0;
    joinedWorkspaces.set(workspaceId, count + 1);
    if (count) return;
    if (current.connected)
      current.emit('workspace.join', { workspaceId, lastEventId: getCursor(workspaceId) });
  }
  function leaveWorkspace(workspaceId: string): void {
    const count = joinedWorkspaces.get(workspaceId) ?? 0;
    if (!count) return;
    if (count > 1) {
      joinedWorkspaces.set(workspaceId, count - 1);
      return;
    }
    joinedWorkspaces.delete(workspaceId);
    workspacePresence.delete(workspaceId);
    for (const [key, lock] of ownedLocks)
      if (lock.workspaceId === workspaceId) {
        ownedLocks.delete(key);
        if (current.connected) current.emit('lock.release', lock);
      }
    if (current.connected) current.emit('workspace.leave', { workspaceId });
  }
  function updatePresence(input: CollaborationPresencePayload): void {
    workspacePresence.set(input.workspaceId, {
      pageId: input.pageId,
      selectedNodeId: input.selectedNodeId,
    });
    if (current.connected) current.emit('presence.update', input);
  }
  function acquireLock(input: { workspaceId: string; resourceKey: string }): void {
    ownedLocks.set(`${input.workspaceId}:${input.resourceKey}`, input);
    if (current.connected) current.emit('lock.acquire', input);
  }
  function releaseLock(input: { workspaceId: string; resourceKey: string }): void {
    ownedLocks.delete(`${input.workspaceId}:${input.resourceKey}`);
    if (current.connected) current.emit('lock.release', input);
  }
  async function joinAgentSession(workspaceId: string, sessionId: string): Promise<void> {
    joinedAgentSessions.set(sessionId, workspaceId);
    if (!current.connected)
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Agent 实时通道连接超时')), 5_000);
        current.once('connect', () => {
          clearTimeout(timeout);
          resolve();
        });
        current.once('connect_error', () => {
          clearTimeout(timeout);
          reject(new Error('Agent 实时通道连接失败'));
        });
        current.connect();
      });
    await new Promise<void>((resolve, reject) => {
      current
        .timeout(5_000)
        .emit(
          'agent-session.join',
          { workspaceId, sessionId },
          (error: Error | null, result?: { sessionId: string }) => {
            if (error || result?.sessionId !== sessionId)
              reject(error ?? new Error('Agent 实时订阅失败'));
            else resolve();
          },
        );
    });
  }
  function leaveAgentSession(sessionId: string): void {
    joinedAgentSessions.delete(sessionId);
    if (current.connected) current.emit('agent-session.leave', { sessionId });
  }
  async function subscribeComponentData(
    input: Omit<ComponentDataSubscription, 'count'>,
  ): Promise<{ channel: string }> {
    const existing = componentDataSubscriptions.get(input.channel);
    if (existing) {
      existing.count += 1;
      return { channel: existing.channel };
    }
    await joinComponentData(current, input);
    componentDataSubscriptions.set(input.channel, {
      ...input,
      count: (componentDataSubscriptions.get(input.channel)?.count ?? 0) + 1,
    });
    return { channel: input.channel };
  }
  function unsubscribeComponentData(input: Omit<ComponentDataSubscription, 'count'>): void {
    const existing = componentDataSubscriptions.get(input.channel);
    if (!existing) return;
    existing.count -= 1;
    if (existing.count > 0) return;
    componentDataSubscriptions.delete(input.channel);
    if (current.connected) current.emit('component-data.unsubscribe', {
      channel: existing.channel, subscriptionToken: existing.subscriptionToken,
    });
  }
  async function refreshComponentDataSubscription(
    input: Omit<ComponentDataSubscription, 'count'>,
  ): Promise<void> {
    const existing = componentDataSubscriptions.get(input.channel);
    if (!existing) return;
    await joinComponentData(current, input);
    if (componentDataSubscriptions.get(input.channel) === existing)
      existing.subscriptionToken = input.subscriptionToken;
    else if (!componentDataSubscriptions.has(input.channel) && current.connected)
      current.emit('component-data.unsubscribe', input);
  }
  function onRealtimeEvent(listener: (event: RealtimeEvent) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
  return {
    agentViewConnectionId: () => current.connected ? current.id : undefined,
    onAgentViewRead,
    connected,
    connect,
    joinWorkspace,
    leaveWorkspace,
    updatePresence,
    acquireLock,
    releaseLock,
    joinAgentSession,
    leaveAgentSession,
    subscribeComponentData,
    unsubscribeComponentData,
    refreshComponentDataSubscription,
    onRealtimeEvent,
  };
}
